import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, ShieldCheck } from "lucide-react";

// ---------------------------------------------------------------------------
// MP12 Module 2B — RouteToReviewDialog
//
// An explicit, per-target routing dialog. Nothing happens unless the
// operator ticks a target checkbox AND ticks the confirmation gate at the
// bottom. Sub-panels for each target unfold only when their toggle is on.
// The dialog does NOT overwrite verified records — it only creates NEW
// candidate records or APPENDS to existing consultation notes.
// ---------------------------------------------------------------------------

export function RouteToReviewDialog({
  open, onOpenChange, workspace, onRouted,
}) {
  const linkedConsId = (workspace?.linked_consultation_id || "").trim();

  const [routingReason, setRoutingReason] = useState("");

  // Candidate track --------------------------------------------------------
  const [wantTrack, setWantTrack] = useState(false);
  const [trackCatalogCode, setTrackCatalogCode] = useState("");
  const [copyParties, setCopyParties] = useState(true);
  const [initialStatusNote, setInitialStatusNote] = useState("");

  // Consultation follow-up -------------------------------------------------
  const [wantConsFollowup, setWantConsFollowup] = useState(false);
  const [consNote, setConsNote] = useState("");
  const [setConsStatus, setSetConsStatus] = useState("");

  // Document request ------------------------------------------------------
  const [wantDocReq, setWantDocReq] = useState(false);
  const [docReqChannel, setDocReqChannel] = useState("");
  const [docReqDueHint, setDocReqDueHint] = useState("");
  const [docReqNote, setDocReqNote] = useState("");
  const [docStatuses, setDocStatuses] = useState({
    DEFICIENT: true, NEEDS_REVIEW: true, NOT_PROVIDED: false,
  });

  // Compliance case -------------------------------------------------------
  const [wantCompliance, setWantCompliance] = useState(false);
  const [complianceNotes, setComplianceNotes] = useState("");
  const [attorneyReview, setAttorneyReview] = useState(false);
  const [attorneyReason, setAttorneyReason] = useState("");

  // Task follow-up --------------------------------------------------------
  const [wantTasks, setWantTasks] = useState(false);

  // Confirmation gate -----------------------------------------------------
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const anyTarget = wantTrack || wantConsFollowup || wantDocReq || wantCompliance || wantTasks;

  const docCounts = useMemo(() => {
    const c = { HAVE: 0, NOT_PROVIDED: 0, NOT_APPLICABLE: 0, NEEDS_REVIEW: 0, DEFICIENT: 0, VERIFIED: 0 };
    for (const it of (workspace?.checklist || [])) {
      const s = (it.status || "NOT_PROVIDED").toUpperCase();
      if (c[s] !== undefined) c[s] += 1;
    }
    return c;
  }, [workspace]);

  const openTaskCount = useMemo(() => {
    return (workspace?.tasks || []).filter((t) => (t?.status || "open") !== "done").length;
  }, [workspace]);

  function reset() {
    setRoutingReason("");
    setWantTrack(false); setTrackCatalogCode(""); setCopyParties(true); setInitialStatusNote("");
    setWantConsFollowup(false); setConsNote(""); setSetConsStatus("");
    setWantDocReq(false); setDocReqChannel(""); setDocReqDueHint(""); setDocReqNote("");
    setDocStatuses({ DEFICIENT: true, NEEDS_REVIEW: true, NOT_PROVIDED: false });
    setWantCompliance(false); setComplianceNotes(""); setAttorneyReview(false); setAttorneyReason("");
    setWantTasks(false);
    setConfirmed(false);
  }

  async function submit() {
    if (!anyTarget) {
      toast.error("Select at least one routing target.");
      return;
    }
    if (!confirmed) {
      toast.error("Confirm you have reviewed the intake before routing.");
      return;
    }
    if (wantCompliance && !wantTrack) {
      toast.error("Compliance case requires the candidate track to be created in the same routing action.");
      return;
    }
    if (wantConsFollowup && !linkedConsId) {
      toast.error("This intake has no linked Consultation Request. Link one first, or uncheck consultation follow-up.");
      return;
    }
    if (wantTrack && !trackCatalogCode.trim()) {
      toast.error("Catalog Code is required when creating a candidate track.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        routing_reason: routingReason.trim(),
        operator_confirmation: true,
        route_to_candidate_track: wantTrack,
        candidate_track: wantTrack ? {
          catalog_code: trackCatalogCode.trim().toUpperCase(),
          copy_rights_parties: copyParties,
          initial_status_note: initialStatusNote.trim(),
        } : null,
        route_to_consultation_followup: wantConsFollowup,
        consultation_followup: wantConsFollowup ? {
          note: consNote.trim(),
          set_consultation_status: setConsStatus.trim(),
        } : null,
        route_to_document_request: wantDocReq,
        document_request: wantDocReq ? {
          item_ids: [],
          include_statuses: Object.entries(docStatuses).filter(([, v]) => v).map(([k]) => k),
          channel: docReqChannel.trim(),
          due_hint: docReqDueHint.trim(),
          note: docReqNote.trim(),
        } : null,
        route_to_compliance_case: wantCompliance,
        compliance_case: wantCompliance ? {
          reviewer_notes: complianceNotes.trim(),
          attorney_review_required: attorneyReview,
          attorney_review_reason: attorneyReason.trim(),
        } : null,
        route_to_task_followup: wantTasks,
        task_followup: wantTasks ? { task_ids: [] } : null,
      };
      const r = await api.post(`/intake-workspaces/${workspace.id}/route`, payload);
      toast.success("Intake routed. Linked records created.");
      onRouted && onRouted(r.data);
      reset();
      onOpenChange && onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Routing failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange && onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="intake-route-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" style={{ color: "hsl(347 60% 22%)" }} />
            Route intake to review
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            Explicitly create the candidate records this intake should route to. Nothing is
            created unless you tick it. Verified records are never overwritten.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-[11px] uppercase tracking-wide">Routing reason (visible in the Proof Ledger)</Label>
            <Textarea
              value={routingReason}
              onChange={(e) => setRoutingReason(e.target.value)}
              rows={2}
              placeholder="Why is this intake being routed now? Any caveats for downstream reviewers?"
              className="mt-1"
              data-testid="intake-route-reason"
            />
          </div>

          {/* Candidate track */}
          <Card>
            <CardContent className="p-3 space-y-2">
              <label className="flex items-center justify-between text-[13px] font-medium">
                <span className="flex items-center gap-2">
                  <Checkbox
                    checked={wantTrack}
                    onCheckedChange={(v) => setWantTrack(!!v)}
                    data-testid="intake-route-track"
                  />
                  Create candidate track
                </span>
                <span className="text-[10.5px] text-muted-foreground uppercase tracking-wide">New record only</span>
              </label>
              {wantTrack && (
                <div className="grid md:grid-cols-2 gap-2 pl-6">
                  <div>
                    <Label className="text-[10.5px] uppercase tracking-wide">Catalog Code (active)</Label>
                    <Input
                      value={trackCatalogCode}
                      onChange={(e) => setTrackCatalogCode(e.target.value)}
                      placeholder="e.g. GFM-INT"
                      className="mt-1 font-mono uppercase"
                      data-testid="intake-route-track-code"
                    />
                  </div>
                  <div className="flex items-center justify-between mt-6 border rounded px-2 py-1.5">
                    <span className="text-[12px]">Copy rights parties into new track</span>
                    <Switch checked={copyParties} onCheckedChange={setCopyParties} data-testid="intake-route-track-copy-parties" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[10.5px] uppercase tracking-wide">Provisional status note</Label>
                    <Input
                      value={initialStatusNote}
                      onChange={(e) => setInitialStatusNote(e.target.value)}
                      placeholder="e.g. Development candidate — clearance not yet reviewed"
                      className="mt-1"
                      data-testid="intake-route-track-status-note"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Consultation follow-up */}
          <Card>
            <CardContent className="p-3 space-y-2">
              <label className="flex items-center justify-between text-[13px] font-medium">
                <span className="flex items-center gap-2">
                  <Checkbox
                    checked={wantConsFollowup}
                    onCheckedChange={(v) => setWantConsFollowup(!!v)}
                    disabled={!linkedConsId}
                    data-testid="intake-route-cons"
                  />
                  Append follow-up note to linked Consultation Request
                </span>
                <span className="text-[10.5px] text-muted-foreground uppercase tracking-wide">
                  {linkedConsId ? `linked · ${linkedConsId.slice(0, 8)}` : "not linked"}
                </span>
              </label>
              {wantConsFollowup && linkedConsId && (
                <div className="pl-6 space-y-2">
                  <div>
                    <Label className="text-[10.5px] uppercase tracking-wide">Follow-up note</Label>
                    <Textarea
                      value={consNote}
                      onChange={(e) => setConsNote(e.target.value)}
                      rows={2}
                      className="mt-1"
                      data-testid="intake-route-cons-note"
                    />
                  </div>
                  <div>
                    <Label className="text-[10.5px] uppercase tracking-wide">Set consultation status (optional)</Label>
                    <Input
                      value={setConsStatus}
                      onChange={(e) => setSetConsStatus(e.target.value)}
                      placeholder="Leave blank to keep current status"
                      className="mt-1"
                      data-testid="intake-route-cons-status"
                    />
                  </div>
                  <p className="text-[10.5px] text-muted-foreground pl-1">
                    The follow-up note is appended to <code className="font-mono">internal_notes</code>. Existing content is preserved.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Document request */}
          <Card>
            <CardContent className="p-3 space-y-2">
              <label className="flex items-center justify-between text-[13px] font-medium">
                <span className="flex items-center gap-2">
                  <Checkbox
                    checked={wantDocReq}
                    onCheckedChange={(v) => setWantDocReq(!!v)}
                    data-testid="intake-route-docreq"
                  />
                  Create document-request list
                </span>
                <span className="text-[10.5px] text-muted-foreground uppercase tracking-wide">
                  DEFICIENT {docCounts.DEFICIENT} · REVIEW {docCounts.NEEDS_REVIEW} · MISSING {docCounts.NOT_PROVIDED}
                </span>
              </label>
              {wantDocReq && (
                <div className="pl-6 space-y-2">
                  <div className="flex flex-wrap gap-3">
                    {["DEFICIENT", "NEEDS_REVIEW", "NOT_PROVIDED"].map((s) => (
                      <label key={s} className="flex items-center gap-1.5 text-[12px]">
                        <Checkbox
                          checked={!!docStatuses[s]}
                          onCheckedChange={(v) => setDocStatuses((prev) => ({ ...prev, [s]: !!v }))}
                          data-testid={`intake-route-docreq-${s}`}
                        />
                        Include <span className="font-mono text-[10.5px]">{s}</span>
                      </label>
                    ))}
                  </div>
                  <div className="grid md:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10.5px] uppercase tracking-wide">Delivery channel</Label>
                      <Input
                        value={docReqChannel}
                        onChange={(e) => setDocReqChannel(e.target.value)}
                        placeholder="email · portal · manual"
                        className="mt-1"
                        data-testid="intake-route-docreq-channel"
                      />
                    </div>
                    <div>
                      <Label className="text-[10.5px] uppercase tracking-wide">Due hint</Label>
                      <Input
                        value={docReqDueHint}
                        onChange={(e) => setDocReqDueHint(e.target.value)}
                        placeholder="e.g. before 2026-08-01"
                        className="mt-1"
                        data-testid="intake-route-docreq-due"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10.5px] uppercase tracking-wide">Operator note (internal)</Label>
                    <Textarea
                      value={docReqNote}
                      onChange={(e) => setDocReqNote(e.target.value)}
                      rows={2}
                      className="mt-1"
                      data-testid="intake-route-docreq-note"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Compliance case */}
          <Card>
            <CardContent className="p-3 space-y-2">
              <label className="flex items-center justify-between text-[13px] font-medium">
                <span className="flex items-center gap-2">
                  <Checkbox
                    checked={wantCompliance}
                    onCheckedChange={(v) => setWantCompliance(!!v)}
                    data-testid="intake-route-compliance"
                  />
                  Open compliance review case
                </span>
                <span className="text-[10.5px] text-muted-foreground uppercase tracking-wide">Requires candidate track</span>
              </label>
              {wantCompliance && (
                <div className="pl-6 space-y-2">
                  <div>
                    <Label className="text-[10.5px] uppercase tracking-wide">Reviewer notes</Label>
                    <Textarea
                      value={complianceNotes}
                      onChange={(e) => setComplianceNotes(e.target.value)}
                      rows={2}
                      className="mt-1"
                      data-testid="intake-route-compliance-notes"
                    />
                  </div>
                  <label className="flex items-center justify-between rounded border p-2 text-[12px]">
                    <span>Attorney review required</span>
                    <Switch checked={attorneyReview} onCheckedChange={setAttorneyReview} data-testid="intake-route-compliance-attorney" />
                  </label>
                  {attorneyReview && (
                    <div>
                      <Label className="text-[10.5px] uppercase tracking-wide">Attorney review reason</Label>
                      <Input
                        value={attorneyReason}
                        onChange={(e) => setAttorneyReason(e.target.value)}
                        className="mt-1"
                        data-testid="intake-route-compliance-attorney-reason"
                      />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Task follow-up */}
          <Card>
            <CardContent className="p-3 space-y-2">
              <label className="flex items-center justify-between text-[13px] font-medium">
                <span className="flex items-center gap-2">
                  <Checkbox
                    checked={wantTasks}
                    onCheckedChange={(v) => setWantTasks(!!v)}
                    disabled={openTaskCount === 0}
                    data-testid="intake-route-tasks"
                  />
                  Persist open intake tasks as follow-up records
                </span>
                <span className="text-[10.5px] text-muted-foreground uppercase tracking-wide">
                  {openTaskCount} open task{openTaskCount === 1 ? "" : "s"}
                </span>
              </label>
              {wantTasks && openTaskCount > 0 && (
                <p className="pl-6 text-[11px] text-muted-foreground">
                  All intake tasks whose status is not <em>done</em> will be persisted as follow-up records.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Confirmation gate */}
          <div
            className="rounded border p-3 flex items-start gap-2"
            style={{ background: "hsl(40 55% 96%)", borderColor: "hsl(40 55% 78%)" }}
          >
            <AlertTriangle className="w-4 h-4 mt-0.5" style={{ color: "hsl(35 60% 30%)" }} />
            <div className="flex-1">
              <label className="flex items-start gap-2 text-[12.5px] cursor-pointer">
                <Checkbox
                  checked={confirmed}
                  onCheckedChange={(v) => setConfirmed(!!v)}
                  data-testid="intake-route-confirm"
                />
                <span>
                  I have reviewed this intake and confirm the routing selections above.
                  I understand that <strong>verified records will not be overwritten</strong> and that
                  routing creates <strong>only</strong> the candidate records I have ticked.
                </span>
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange && onOpenChange(false)} disabled={busy} data-testid="intake-route-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !anyTarget || !confirmed}
            style={{ background: "hsl(347 72% 22%)" }}
            data-testid="intake-route-submit"
          >
            {busy ? "Routing…" : "Route intake"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RouteToReviewDialog;
